import React, { useState, useEffect } from 'react';
import { RefreshCw, CheckCircle, ClipboardList, ChevronRight } from 'lucide-react';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

interface CompanyProfile {
  id: number;
  company_name: string;
  sector: string;
  sub_sector: string;
  employee_count: number;
  annual_turnover: number;
  turnover_currency: string;
  registered_country: string;
  fca_regulated: boolean;
  aml_supervised: boolean;
  processes_personal_data: boolean;
  processes_special_category_data: boolean;
  has_international_transfers: boolean;
  listed_company: boolean;
  charity: boolean;
  nhs_supplier: boolean;
  completed_at: string;
}

interface Questionnaire {
  id: number;
  title: string;
  description: string;
  questions: Question[];
}

interface Question {
  id: string;
  text: string;
  type: 'text' | 'number' | 'boolean' | 'select' | 'multi_select';
  field_key: string;
  options?: string[];
  required: boolean;
  help_text?: string;
}

interface ApplicabilityResult {
  framework: string;
  obligation: string;
  applicable: boolean;
  reason: string;
  confidence: string;
}

const OnboardingQuestionnaire: React.FC = () => {
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [applicability, setApplicability] = useState<ApplicabilityResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeQuestionnaire, setActiveQuestionnaire] = useState<Questionnaire | null>(null);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [submitting, setSubmitting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [evaluating, setEvaluating] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setIsLoading(true);
    setError('');
    try {
      const [profileRes, questionnaireRes] = await Promise.all([
        fetch('/api/compliance/onboarding/profile', { headers: authHeaders() }).then(r => r.json()),
        fetch('/api/compliance/onboarding/questionnaires', { headers: authHeaders() }).then(r => r.json()),
      ]);
      setProfile(profileRes.profile || null);
      setQuestionnaires(questionnaireRes.questionnaires || []);
      if (profileRes.applicability) {
        setApplicability(profileRes.applicability || []);
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitQuestionnaire = async () => {
    if (!activeQuestionnaire) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch('/api/compliance/onboarding/profile', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ questionnaire_id: activeQuestionnaire.id, answers }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to submit');
      const data = await res.json();
      setProfile(data.profile || null);
      if (data.applicability) setApplicability(data.applicability);
      setActiveQuestionnaire(null);
      setAnswers({});
      setSuccess('Questionnaire submitted successfully.');
      setTimeout(() => setSuccess(''), 4000);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerateObligations = async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await fetch('/api/compliance/onboarding/generate-obligations', {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to generate');
      const data = await res.json();
      setSuccess(`Generated ${data.count || 0} obligations based on your profile.`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleReEvaluate = async () => {
    setEvaluating(true);
    setError('');
    try {
      const res = await fetch('/api/compliance/onboarding/re-evaluate', {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to re-evaluate');
      const data = await res.json();
      setApplicability(data.applicability || []);
      setSuccess('Applicability rules re-evaluated.');
      setTimeout(() => setSuccess(''), 4000);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setEvaluating(false);
    }
  };

  const renderAnswer = (question: Question) => {
    const val = answers[question.field_key];
    switch (question.type) {
      case 'boolean':
        return (
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" name={question.field_key} checked={val === true} onChange={() => setAnswers(a => ({ ...a, [question.field_key]: true }))} />
              Yes
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" name={question.field_key} checked={val === false} onChange={() => setAnswers(a => ({ ...a, [question.field_key]: false }))} />
              No
            </label>
          </div>
        );
      case 'number':
        return <input type="number" value={val || ''} onChange={e => setAnswers(a => ({ ...a, [question.field_key]: Number(e.target.value) }))} className="w-full border rounded-lg px-3 py-2 text-sm" />;
      case 'select':
        return (
          <select value={val || ''} onChange={e => setAnswers(a => ({ ...a, [question.field_key]: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
            <option value="">Select...</option>
            {question.options?.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        );
      case 'multi_select':
        return (
          <div className="flex flex-wrap gap-2">
            {question.options?.map(o => (
              <label key={o} className="flex items-center gap-1.5 text-sm">
                <input type="checkbox" checked={(val || []).includes(o)} onChange={e => {
                  const current = val || [];
                  setAnswers(a => ({ ...a, [question.field_key]: e.target.checked ? [...current, o] : current.filter((v: string) => v !== o) }));
                }} />
                {o}
              </label>
            ))}
          </div>
        );
      default:
        return <input type="text" value={val || ''} onChange={e => setAnswers(a => ({ ...a, [question.field_key]: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />;
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClipboardList className="w-7 h-7 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Compliance Onboarding</h1>
            <p className="text-sm text-gray-500 mt-0.5">Scope your regulatory obligations based on your company profile</p>
          </div>
        </div>
        <button onClick={loadAll} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
          <RefreshCw size={16} />
        </button>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}
      {success && <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">{success}</div>}

      {/* Questionnaire Form */}
      {activeQuestionnaire ? (
        <div className="bg-white border rounded-xl p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{activeQuestionnaire.title}</h2>
              {activeQuestionnaire.description && <p className="text-sm text-gray-500 mt-1">{activeQuestionnaire.description}</p>}
            </div>
            <button onClick={() => { setActiveQuestionnaire(null); setAnswers({}); }} className="text-sm text-gray-500 hover:text-gray-700">Cancel</button>
          </div>
          <div className="space-y-6">
            {activeQuestionnaire.questions?.map((q, i) => (
              <div key={q.id} className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {i + 1}. {q.text} {q.required && <span className="text-red-500">*</span>}
                </label>
                {q.help_text && <p className="text-xs text-gray-400">{q.help_text}</p>}
                {renderAnswer(q)}
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button onClick={() => { setActiveQuestionnaire(null); setAnswers({}); }} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={handleSubmitQuestionnaire} disabled={submitting} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {submitting ? 'Submitting...' : 'Submit Questionnaire'}
            </button>
          </div>
        </div>
      ) : profile?.completed_at ? (
        /* Profile Summary */
        <>
          <div className="bg-white border rounded-xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle size={20} className="text-green-600" />
                <h2 className="text-lg font-semibold text-gray-900">Company Profile</h2>
              </div>
              <span className="text-xs text-gray-400">Completed {new Date(profile.completed_at).toLocaleDateString()}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              {[
                { label: 'Sector', value: profile.sector },
                { label: 'Sub-Sector', value: profile.sub_sector },
                { label: 'Employees', value: profile.employee_count?.toLocaleString() },
                { label: 'Annual Turnover', value: profile.annual_turnover ? `${profile.turnover_currency || 'GBP'} ${profile.annual_turnover.toLocaleString()}` : '—' },
                { label: 'Registered Country', value: profile.registered_country },
                { label: 'FCA Regulated', value: profile.fca_regulated ? 'Yes' : 'No' },
                { label: 'AML Supervised', value: profile.aml_supervised ? 'Yes' : 'No' },
                { label: 'Processes Personal Data', value: profile.processes_personal_data ? 'Yes' : 'No' },
                { label: 'Special Category Data', value: profile.processes_special_category_data ? 'Yes' : 'No' },
                { label: 'International Transfers', value: profile.has_international_transfers ? 'Yes' : 'No' },
                { label: 'Listed Company', value: profile.listed_company ? 'Yes' : 'No' },
                { label: 'NHS Supplier', value: profile.nhs_supplier ? 'Yes' : 'No' },
              ].map(({ label, value }) => (
                <div key={label}>
                  <p className="text-gray-500">{label}</p>
                  <p className="font-medium text-gray-900">{value || '—'}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Applicability Results */}
          <div className="bg-white border rounded-xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Applicability Results</h2>
              <div className="flex gap-2">
                <button onClick={handleReEvaluate} disabled={evaluating} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50 disabled:opacity-50">
                  {evaluating ? 'Re-evaluating...' : 'Re-evaluate'}
                </button>
                <button onClick={handleGenerateObligations} disabled={generating} className="flex items-center gap-1 px-3 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {generating ? 'Generating...' : 'Generate Obligations'}
                </button>
              </div>
            </div>
            {applicability.length === 0 ? (
              <p className="text-sm text-gray-500">No applicability results yet. Click "Re-evaluate" to run the rules engine.</p>
            ) : (
              <div className="divide-y">
                {applicability.map((a, i) => (
                  <div key={i} className="py-3 flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{a.framework || a.obligation}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{a.reason}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-4">
                      {a.confidence && <span className="text-xs text-gray-400">{a.confidence}</span>}
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${a.applicable ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                        {a.applicable ? 'Applicable' : 'Not Applicable'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        /* No profile yet — show questionnaire options */
        <div className="space-y-4">
          {questionnaires.length === 0 ? (
            <div className="bg-white border rounded-xl p-8">
              <ClipboardList size={48} className="mx-auto text-gray-300 mb-4" />
              <h2 className="text-lg font-semibold text-gray-700 mb-1">No Questionnaires Available</h2>
              <p className="text-sm text-gray-500">Contact your administrator to set up the onboarding questionnaire.</p>
            </div>
          ) : (
            <>
              <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg text-sm">
                Complete the onboarding questionnaire to identify which regulations and obligations apply to your organisation.
              </div>
              {questionnaires.map(q => (
                <div key={q.id} className="bg-white border rounded-xl p-5 flex items-center justify-between hover:border-blue-300 cursor-pointer transition-colors" onClick={() => setActiveQuestionnaire(q)}>
                  <div>
                    <h3 className="font-medium text-gray-900">{q.title}</h3>
                    {q.description && <p className="text-sm text-gray-500 mt-0.5">{q.description}</p>}
                    <p className="text-xs text-gray-400 mt-1">{q.questions?.length || 0} questions</p>
                  </div>
                  <ChevronRight size={20} className="text-gray-400" />
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default OnboardingQuestionnaire;
